import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, DatePipe, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { RouterModule } from '@angular/router';

import { ImageService } from '../../../services/image.service';
import { BlogService } from '../../../services/blog.service';
import { BlogPost } from '../../../models/blog-post.models';
import { Herb } from '../../../models/herb.model';
import { HERBS } from '../../../data/herbs';
import { LoadingSpinnerComponent } from '../../../ui/loading-spinner/loading-spinner.component';
import { siteConfig } from '../../../config/site-config';

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule, RouterModule, LoadingSpinnerComponent, NgOptimizedImage],
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.css'],
  providers: [DatePipe],
})
export class HeroComponent implements OnInit {
  heroImageSrc = '';
  readonly infoCategorySlug = siteConfig.content.defaultInfoCategorySlug;
  readonly blogPath = siteConfig.routes.blogPath;

  loading = false;
  months: string[] = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  currentDate: Date = new Date();
  currentMonthIndex: number = this.currentDate.getMonth();

  isBrowser: boolean;
  isLoading = true;

  herbOfTheDay: Herb;
  recommendedArticles: BlogPost[] = [];

  constructor(
    public imageService: ImageService,
    private blogService: BlogService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.herbOfTheDay = this.initHerbOfTheDay();
    this.updateHeroImageSources();
    this.initRecommendedArticles();
  }

  ngOnInit(): void {}

  private updateHeroImageSources(): void {
    const imageUrl = this.herbOfTheDay?.imageUrl;

    if (!imageUrl) {
      this.heroImageSrc = '/assets/images/fallback.webp';
      return;
    }

    this.heroImageSrc = this.imageService.getOptimizedImageUrl(imageUrl, {
      width: 960,
      quality: 82,
      format: 'auto'
    });

  }

  private getDayOfYear(date: Date): number {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  }

  advanceToNextDay(): void {
    const nextDay = new Date(this.currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    this.currentDate = nextDay;
    this.currentMonthIndex = this.currentDate.getMonth();
    this.herbOfTheDay = this.initHerbOfTheDay();
    this.updateHeroImageSources();
    this.initRecommendedArticles();
  }

  private initHerbOfTheDay(): Herb {
    const dayOfYear = this.getDayOfYear(this.currentDate);
    const year = this.currentDate.getFullYear();
    const seedValue = dayOfYear + year;

    const herbsInSeason = HERBS.filter((herb) => this.isHarvestable(herb, this.currentMonthIndex));
    const peakHerbs = herbsInSeason.filter((herb) => this.isInPeakSeason(herb));

    let herbList = HERBS;
    if (peakHerbs.length > 0) {
      herbList = peakHerbs;
    } else if (herbsInSeason.length > 0) {
      herbList = herbsInSeason;
    }

    const herbIndex = seedValue % herbList.length;
    return herbList[herbIndex];
  }

  private initRecommendedArticles(): void {
    if (!this.herbOfTheDay) return;

    this.loading = true;

    this.blogService.getRandomPosts(this.herbOfTheDay.name).subscribe({
      next: (response) => {
        this.recommendedArticles = response.posts;
        this.loading = false;
      },
      error: (error) => {
        console.error('Fehler beim Laden der empfohlenen Artikel:', error);
        this.loading = false;
      }
    });
  }

  isHarvestable(herb: Herb, monthIndex: number): boolean {
    return herb?.harvestMonths?.includes(monthIndex + 1) || false;
  }

  isInPeakSeason(herb: Herb): boolean {
    return herb?.peakMonths?.includes(this.currentMonthIndex + 1) || false;
  }

  isPeakSeason(herb: Herb, monthIndex: number): boolean {
    return herb?.peakMonths?.includes(monthIndex + 1) || false;
  }

  isCurrentMonth(monthIndex: number): boolean {
    return monthIndex === this.currentMonthIndex;
  }

  getShortUsesText(herb: Herb): string {
    if (!herb) return '';

    if (typeof herb.uses === 'string') {
      return herb.uses.length > 100 ? herb.uses.substring(0, 100) + '...' : herb.uses;
    }

    if (Array.isArray(herb.uses)) {
      const usesText = herb.uses.join(', ');
      return usesText.length > 100 ? usesText.substring(0, 100) + '...' : usesText;
    }

    return 'Keine Angaben zur Verwendung.';
  }
}
